import React from 'react';
import {
	CTAScreen,
	CustomHero,
	ImageTextSection,
	Reviews,
} from '@/components/landing/reusableSections';
import { Columns } from '@/components/landing/reusableSections/Columns';
import {
	useLandingShownEvent,
	useSendToAmplitudeScrolledPercent,
} from '@/hooks';
import { useTranslationWithLocale } from '@/hooks/useTranslationWithLocale';
import { GenerateLandingLinkService, GoogleService } from '@/services';
import {
	challengesAboveList,
	forBusiness_furniture_challenges,
	forBusiness_furniture_crafted,
	forBusiness_furniture_elevate,
	forBusiness_furniture_perfect,
	forBusiness_furniture_tips,
	forBusiness_furniture_use,
	reviewsLanding,
} from '@/data';
import {
	AMPLITUDE_EVENTS,
	GOOGLE_EVENTS,
	LANDING_AMPLITUDE_EVENTS,
	LOCAL_STORAGE_KEYS,
} from '@/constants';
import challenges from '@/public/images/for-business/furniture/challenges.webp';
import crafted from '@/public/images/for-business/furniture/crafted.webp';
import tips from '@/public/images/for-business/furniture/effective.webp';
import imgHero from '@/public/images/for-business/furniture/hero.webp';
import perfect from '@/public/images/for-business/furniture/perfect.webp';
import use from '@/public/images/for-business/furniture/use.webp';
import ctaScreen from '@/public/images/landing/ctaScreen.webp';
import styles from './FurnitureImagesLanding.module.scss';

const FurnitureImagesLanding: React.FC = () => {
	const { t } = useTranslationWithLocale('furniture-images', 'en');
	const { t: tReviews } = useTranslationWithLocale('landing-reviews', 'en');

	const goToProduct = () => {
		GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
		window.location.href = GenerateLandingLinkService.webApp();
	};

	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		LOCAL_STORAGE_KEYS.FURNITURE_IMAGES_SCROLLED_POINTS,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: LANDING_AMPLITUDE_EVENTS.FURNITURE_IMAGES_LANDING },
	);

	useLandingShownEvent({
		type: LANDING_AMPLITUDE_EVENTS.FURNITURE_IMAGES_LANDING,
	});

	return (
		<>
			<CustomHero
				image={imgHero}
				title={t('heroData_title')}
				subtitle={t('heroData_subtitle')}
				onClick={goToProduct}
				buttonText={t('getStarted')}
				classes={{
					mainTitle: styles['furniture__hero-title'],
					mainSubtitle: styles['furniture__hero-subtitle'],
				}}
			/>

			{/* challenges */}
			<ImageTextSection
				title={t('challengesData_mainTitle')}
				textsAboveList={challengesAboveList}
				image={challenges}
				data={forBusiness_furniture_challenges}
				extraTextBelow={t('challengesData_extraText')}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
			/>

			{/* crafted */}
			<ImageTextSection
				title={t('craftedData_mainTitle')}
				subtitle1={t('craftedData_mainSubtitle1')}
				image={crafted}
				data={forBusiness_furniture_crafted}
				extraTextBelow={t('craftedData_extraText')}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					imageContainer: styles['furniture__crafted-image-container'],
					list: styles['furniture__crafted-list'],
				}}
			/>

			<Reviews t={tReviews} data={reviewsLanding} />

			{/* elevate */}
			<Columns
				t={t}
				title={t('elevateData_mainTitle')}
				data={forBusiness_furniture_elevate}
				buttonText={t('getStarted')}
				onClick={goToProduct}
				classes={{
					columnTitle: styles['furniture__elevate-column-title'],
				}}
			/>

			{/* use */}
			<ImageTextSection
				title={t('useData_mainTitle')}
				image={use}
				data={forBusiness_furniture_use}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					section: styles['furniture__use-section'],
				}}
			/>

			{/* tips */}
			<ImageTextSection
				title={t('tipsData_mainTitle')}
				image={tips}
				data={forBusiness_furniture_tips}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					imageContainer: styles['furniture__tips-image-container'],
					list: styles['furniture__tips-list'],
					section: styles['furniture__tips-section'],
				}}
			/>

			{/* perfect for */}
			<ImageTextSection
				title={t('perfectData_mainTitle')}
				image={perfect}
				data={forBusiness_furniture_perfect}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
			/>

			{/* TryLift */}
			<CTAScreen
				title1={t('tryLiftData_title1')}
				title_green={t('tryLiftData_greenText')}
				title2={t('tryLiftData_title2')}
				subtitle={t('tryLiftData_subtitle')}
				image={ctaScreen}
				altText={t('tryLiftData_altText')}
				buttonText={t('getStarted')}
				onClick={goToProduct}
				classes={{
					title: styles['furniture__tryLift-title'],
					subtitleClass: styles['furniture__tryLift-subtitle'],
				}}
			/>
		</>
	);
};

export default FurnitureImagesLanding;
